import {useAlert} from './AlertContext';
import {formatDateUTC} from "../utils/formatDateUTC";
import Table from '../Components/Tables';

function AgentAuditOperationDetail({agentAudit, onClose}) {
  const {showAlert} = useAlert();

  const makeBold = (text, weight = 700) =>
    `<span style="font-weight: ${weight};">${text}</span>`;

  const escapeHTML = (str = "") =>
    String(str).replace(/[&<>"]/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;",
    }[c]));

  const formatMarkdown = (text) => {
    if (!text) return "";
    return escapeHTML(text)
      .replace(/\*\*(.*?)\*\*/g, (_, content) => makeBold(content, 500))
      .replace(/\*(?![*])(.*?)\*/g, "<em>$1</em>")
      .replace(/\n/g, "<br>");
  };

  const renderTools = (tools) => {
    if (!Array.isArray(tools) || tools.length === 0) return "";

    return tools.map(tool => {
      let toolBlock = `${makeBold("Tool name:", 500)} ${tool.name}<br>`;

      if (tool.args && typeof tool.args === "object") {
        for (const [key, value] of Object.entries(tool.args)) {
          toolBlock += `${makeBold(key, 500)}: ${value}<br>`;
        }
      } else if (tool.args != null) {
        toolBlock += `${tool.args}<br>`;
      }
      return toolBlock;
    }).join("<br>");
  };

  const renderReasoning = (reasoningData) => {
    const summary = reasoningData?.summary;

    if (!Array.isArray(summary)) return "";

    const text = summary.map(item => item.text || "").join("\n").trim();

    if (!text) return "";
    return `${makeBold("--- Reasoning ---", 500)}<br>${formatMarkdown(text)}`;
  };

  const extractRawContent = (content) => {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
      return content.map(c => c.text || JSON.stringify(c)).join(" ");
    }
    return content?.text || (content ? JSON.stringify(content) : "");
  };

  function mapMessageContent(message, type) {
    const toolBlock = renderTools(message?.tool_calls);

    const reasoningBlock = type === "ai"
      ? renderReasoning(message.additional_kwargs?.reasoning)
      : "";

    const rawContent = extractRawContent(message.content);
    const contentBlock = (type === "ai" || type === "system")
      ? formatMarkdown(rawContent)
      : escapeHTML(rawContent);

    return [toolBlock, contentBlock, reasoningBlock]
      .filter(Boolean)
      .join("<br><br>");
  }

  const trace = (agentAudit.trace_messages || []).map(msg => ({
    type: msg.type,
    description: mapMessageContent(msg, msg.type),
  }));

  const getPreviewText = (html, maxLength = 50) => {
    if (!html) return "";

    if (typeof document !== "undefined" && document.createElement) {
      const div = document.createElement("div");
      div.innerHTML = html;
      const text = (div.textContent || div.innerText || "").trim();
      return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
    }

    // Fallback for non-browser environments: minimal best-effort stripping
    const text = String(html).replace(/<|>/g, "").trim();
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  const columns = [
    { name: "Type", selector: row => row.type, sortable: false },
    { name: "Content", selector: row => getPreviewText(row.description), sortable: false },
  ];

  return (
    <div className="d-flex flex-column h-100">
      <h2 className='h4-title'>{"Agent audit operations"}</h2>

      <div style={{flexGrow: 1, paddingTop: '1rem'}}>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Duration</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <p>
              {agentAudit.duration}
            </p>
          </div>
        </div>
        <div className="input-block d-flex flex-row justify-between">
          <div className="input-description">
            <div className="input-title">Created At</div>
          </div>
          <div className="align-wrapper d-flex align-center">
            <p>{formatDateUTC(agentAudit.created_at)}</p>
          </div>
        </div>
        <div>
          <Table
            title=""
            columns={columns}
            data={trace}
            expandableRows={true}
            dense={false}
            noTableHead={false}
            pagination={false}
          />
        </div>
      </div>
    </div>
  );
}

export default AgentAuditOperationDetail;
